import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import formatDate from "../utils/date";

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 11,
    fontFamily: "Helvetica",
    color: "#111",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 20,
  },
  logoBox: {
    flexDirection: "column",
    flex: 1,
    alignItems: "flex-start",
  },
  logo: {
    height: 50,
    maxWidth: 140,
    objectFit: "contain",
    marginBottom: 6,
  },
  companyText: {
    fontSize: 10,
    marginBottom: 2,
  },
  invoiceBox: {
    flexDirection: "column",
    alignItems: "flex-end",
    flex: 1,
  },
  section: {
    marginBottom: 14,
  },
  bold: {
    fontWeight: "bold",
  },
  tableHeader: {
    flexDirection: "row",
    borderBottom: "1pt solid #ccc",
    paddingVertical: 6,
  },
  tableRow: {
    flexDirection: "row",
    borderBottom: "0.5pt solid #eee",
    paddingVertical: 4,
  },
  col: {
    flex: 1,
    textAlign: "left",
    paddingRight: 5,
  },
  rightAlign: {
    textAlign: "right",
  },
  totalBox: {
    marginTop: 12,
    paddingTop: 8,
    borderTop: "1pt solid #ccc",
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 2,
  },
  notesBox: {
    marginTop: 14,
    padding: 8,
    border: "1pt solid #ddd",
    borderRadius: 4,
  },
  footerText: {
    fontSize: 9,
    color: "#555",
    textAlign: "center",
    marginTop: 20,
  },
});

export default function InvoicePDF({ invoice }) {
  const {
    invoiceNumber,
    invoiceDate,
    dueDate,
    orderNumber,
    accountRef,
    items,
    subtotal,
    vatTotal,
    totalAmount,
    currency,
    companyAddress,
    clientName,
    clientAddress,
    clientCompany,
    companyName,
    companyNumber,
    bankDetails,
    notes,
    vatNote,
    logoUrl,
  } = invoice;

  const getSymbol = (code) => {
    switch (code) {
      case "EUR":
        return "€";
      case "GBP":
        return "£";
      case "USD":
        return "$";
      default:
        return "";
    }
  };

  const symbol = getSymbol(currency);

  const formatCurrency = (value) => {
    return `${symbol} ${parseFloat(value).toLocaleString("en-IE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.logoBox}>
            {logoUrl && <Image src={logoUrl} style={styles.logo} />}
            {companyName && (
              <Text style={[styles.companyText, styles.bold]}>
                {companyName}
              </Text>
            )}
            {companyNumber && (
              <Text style={styles.companyText}>
                Company No: {companyNumber}
              </Text>
            )}
            {companyAddress && (
              <Text style={styles.companyText}>{companyAddress}</Text>
            )}
          </View>

          <View style={styles.invoiceBox}>
            <Text style={styles.bold}>Invoice #{invoiceNumber}</Text>
            <Text>Date: {formatDate(invoiceDate)}</Text>
            {dueDate && <Text>Due: {formatDate(dueDate)}</Text>}
            {orderNumber && <Text>Order: {orderNumber}</Text>}
            {accountRef && <Text>Account Ref: {accountRef}</Text>}
          </View>
        </View>

        {/* Client Info */}
        <View style={styles.section}>
          <Text style={styles.bold}>Bill To</Text>
          {clientName && <Text>{clientName}</Text>}
          {clientCompany && <Text>{clientCompany}</Text>}
          {clientAddress &&
            clientAddress
              .split("\n")
              .map((line, idx) => <Text key={idx}>{line}</Text>)}
        </View>

        {/* Table */}
        <View style={styles.tableHeader}>
          <Text style={[styles.col, { flex: 3, fontWeight: "bold" }]}>
            Description
          </Text>
          <Text style={[styles.col, styles.rightAlign, styles.bold]}>Qty</Text>
          <Text style={[styles.col, styles.rightAlign, styles.bold]}>Rate</Text>
          <Text style={[styles.col, styles.rightAlign, styles.bold]}>Net</Text>
          <Text style={[styles.col, styles.rightAlign, styles.bold]}>VAT</Text>
        </View>

        {(items || []).map((item, idx) => {
          const quantity = parseFloat(item.quantity) || 0;
          const rate = parseFloat(item.unitPrice) || 0;
          const vatRate = parseFloat(item.vat) || 0;
          const net = quantity * rate;
          const vat = (vatRate / 100) * net;

          return (
            <View key={idx} style={styles.tableRow}>
              <Text style={[styles.col, { flex: 3 }]}>{item.description}</Text>
              <Text style={[styles.col, styles.rightAlign]}>{quantity}</Text>
              <Text style={[styles.col, styles.rightAlign]}>
                {formatCurrency(rate)}
              </Text>
              <Text style={[styles.col, styles.rightAlign]}>
                {formatCurrency(net)}
              </Text>
              <Text style={[styles.col, styles.rightAlign]}>
                {formatCurrency(vat)}
              </Text>
            </View>
          );
        })}

        {/* Totals */}
        <View style={styles.totalBox}>
          <View style={styles.totalRow}>
            <Text>Total Net</Text>
            <Text>{formatCurrency(subtotal)}</Text>
          </View>
          <View style={styles.totalRow}>
            <Text>Total VAT</Text>
            <Text>{formatCurrency(vatTotal)}</Text>
          </View>
          <View style={styles.totalRow}>
            <Text style={styles.bold}>Total</Text>
            <Text style={styles.bold}>{formatCurrency(totalAmount)}</Text>
          </View>
        </View>

        {/* VAT Note */}
        {vatNote && (
          <View style={styles.section}>
            <Text>{vatNote}</Text>
          </View>
        )}

        {/* Bank Details */}
        {bankDetails && (
          <View style={styles.notesBox}>
            <Text style={styles.bold}>Bank Details</Text>
            {bankDetails.split("\n").map((line, idx) => (
              <Text key={idx}>{line}</Text>
            ))}
          </View>
        )}

        {/* Notes */}
        {notes && (
          <View style={styles.notesBox}>
            <Text style={styles.bold}>Notes</Text>
            {notes.split("\n").map((line, idx) => (
              <Text key={idx}>{line}</Text>
            ))}
          </View>
        )}

        {/* Footer  */}
        <Text style={styles.footerText}>
          Built by SortlyBooks.com — The smart way to handle your invoices
        </Text>
      </Page>
    </Document>
  );
}
